import { chooseBotAction, type BotDifficulty } from "@guandan/bot-engine";
import { DEFAULT_AVATAR_CONFIG, type AvatarConfig } from "@guandan/avatar-core";
import {
  InvalidGameActionError,
  NATURAL_RANKS,
  applyGameAction,
  classifyCombination,
  createBotObservation,
  createGameState,
  enumerateLegalPlays,
  getCard,
  legalReturnTributeCards,
  legalTributeCards,
  shuffleAndDeal,
  type CardId,
  type GameAction,
  type GameState,
  type GuandanRuleProfile,
  type LegalPlay,
  type NaturalRank,
  type Seat,
} from "@guandan/game-core";
import {
  AvatarConfig as AvatarConfigSchema,
  ReplayManifest as ReplayManifestSchema,
  ServerMessage as ServerMessageSchema,
  ViewerSnapshot as ViewerSnapshotSchema,
  type ClientAction,
  type PublicPlayer,
  type RevealedHands,
  type RevealedHandsByRound,
  type ServerMessage,
  type ViewerSnapshot,
} from "@guandan/protocol";
import { getSessionToken } from "../lib/session";

export interface TableAuthority {
  connect(): Promise<ViewerSnapshot>;
  send(action: ClientAction): Promise<ViewerSnapshot>;
  replay(): Promise<AuthorityReplay>;
  subscribe(listener: (snapshot: ViewerSnapshot) => void): () => void;
  subscribeEvents(listener: (event: AuthorityEvent) => void): () => void;
  suspend?(): void;
  resume?(): Promise<ViewerSnapshot>;
  close(): void;
}

export interface AuthorityEvent {
  readonly seq: number;
  readonly type: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly createdAt: number;
}

export interface AuthorityReplay {
  readonly events: readonly AuthorityEvent[];
  readonly revealedHandsByRound?: RevealedHandsByRound;
}

export type ClientIntent = {
  [Kind in ClientAction["type"]]: Omit<
    Extract<ClientAction, { type: Kind }>,
    "actionId" | "expectedVersion" | "roomId"
  >;
}[ClientAction["type"]];

const names = ["清风徐来", "云间少年", "星河小鹿", "一川烟雨"] as const;
const combinationLabels: Record<string, string> = {
  single: "单张",
  pair: "对子",
  triple: "三张",
  "full-house": "三带二",
  straight: "顺子",
  "consecutive-pairs": "三连对",
  "steel-plate": "钢板",
  bomb: "炸弹",
  "straight-flush": "同花顺",
  "joker-bomb": "四王炸",
};

function lastReplayRound(events: readonly AuthorityEvent[]): number {
  let latest = 1;
  for (const event of events) {
    if (event.type !== "ROUND_STARTED" && event.type !== "CARDS_DEALT")
      continue;
    const round = event.payload.roundNumber;
    if (Number.isInteger(round) && Number(round) > latest)
      latest = Number(round);
  }
  return latest;
}

function levelRank(value: string): NaturalRank {
  return (NATURAL_RANKS as readonly string[]).includes(value)
    ? (value as NaturalRank)
    : "2";
}

function pendingTribute(
  game: GameState,
): { actorSeat: Seat; kind: "offer" | "return"; toSeat: Seat | null } | null {
  if (game.phase !== "tribute" || !game.tribute) return null;
  const donor = game.tribute.donors.find(
    (seat) => !game.tribute?.offers.some((offer) => offer.from === seat),
  );
  if (donor !== undefined)
    return { actorSeat: donor, kind: "offer", toSeat: null };
  const transfer = game.tribute.transfers.find(
    (item) => item.returnCardId === null,
  );
  return transfer
    ? { actorSeat: transfer.to, kind: "return", toSeat: transfer.from }
    : null;
}

function localizedGameError(error: InvalidGameActionError): Error {
  const messages: Record<string, string> = {
    NOT_YOUR_TURN: "还没轮到你",
    INVALID_CARDS: "请选择不同的实体牌",
    CARD_NOT_OWNED: "所选牌已变化，请重新选择",
    INVALID_COMBINATION: "所选牌型不合法或无法压过桌面牌",
    CANNOT_PASS: "首家必须出牌",
    INVALID_PHASE: "当前阶段不能执行此动作",
    TRIBUTE_NOT_MAXIMUM: "进贡必须选择手中点力最大的牌",
    INVALID_RETURN_CARD: "还贡只能选择十点及以下且非级牌",
  };
  return new Error(messages[error.code] ?? error.message);
}

export class LocalTableAuthority implements TableAuthority {
  private game: GameState;
  private roomVersion = 0;
  private paused = false;
  private pausedAt: number | null = null;
  private tableMuted = false;
  private readonly processed = new Map<string, ViewerSnapshot>();
  private readonly listeners = new Set<(snapshot: ViewerSnapshot) => void>();
  private readonly eventListeners = new Set<(event: AuthorityEvent) => void>();
  private lastPublishedEventSeq = -1;
  private closed = false;
  private selfTrustee = false;
  private automationTimer: TimerHandle | null = null;

  constructor(
    private readonly roomId = "practice-local",
    private readonly botDifficulty: BotDifficulty = "expert",
    private readonly selfNickname = "本机玩家",
    private readonly selfAvatar: AvatarConfig = DEFAULT_AVATAR_CONFIG,
    private readonly ruleProfile: Partial<GuandanRuleProfile> = {
      finishMode: "through-ace",
    },
  ) {
    this.game = createGameState({
      matchId: `local-${roomId}`,
      seed: `${roomId}:${crypto.randomUUID()}`,
      now: Date.now(),
      turnDurationMs: 60_000,
      ruleProfile: this.ruleProfile,
      controls: { 0: "human", 1: "bot", 2: "bot", 3: "bot" },
    });
  }

  connect(): Promise<ViewerSnapshot> {
    this.closed = false;
    const snapshot = this.project();
    this.publish(snapshot);
    this.publishNewEvents();
    this.scheduleAutomation();
    return Promise.resolve(snapshot);
  }

  send(action: ClientAction): Promise<ViewerSnapshot> {
    if (this.closed) return Promise.reject(new Error("牌桌连接已关闭"));
    const previous = this.processed.get(action.actionId);
    if (previous) return Promise.resolve(structuredClone(previous));
    try {
      if (action.roomId !== this.roomId)
        throw new Error("房间已失效，请返回大厅重试");
      if (action.expectedVersion !== this.roomVersion)
        throw new Error("状态已更新，正在同步最新牌桌");
      this.apply(action);
      this.startNextRoundIfNeeded();
      const snapshot = this.project();
      this.processed.set(action.actionId, snapshot);
      this.publish(snapshot);
      this.publishNewEvents();
      this.scheduleAutomation();
      return Promise.resolve(structuredClone(snapshot));
    } catch (error) {
      if (error instanceof InvalidGameActionError)
        return Promise.reject(localizedGameError(error));
      return Promise.reject(
        error instanceof Error ? error : new Error("牌桌动作失败"),
      );
    }
  }

  close(): void {
    this.closed = true;
    this.clearAutomationTimer();
    this.listeners.clear();
    this.eventListeners.clear();
  }

  /** Pauses local automation while the PWA is backgrounded or remounting. */
  suspend(): void {
    if (this.closed) return;
    this.clearAutomationTimer();
  }

  resume(): Promise<ViewerSnapshot> {
    return this.connect();
  }

  replay(): Promise<AuthorityReplay> {
    if (this.game.phase !== "match-settled")
      return Promise.reject(new Error("牌局结束前不能查看完整回放"));
    const revealedHandsByRound = Object.fromEntries(
      this.game.events.flatMap((event) => {
        if (event.type !== "CARDS_DEALT") return [];
        const roundNumber = event.payload.roundNumber;
        const seed = event.payload.seed;
        const dealerSeat = event.payload.dealerSeat;
        if (
          !Number.isInteger(roundNumber) ||
          typeof seed !== "string" ||
          (dealerSeat !== 0 &&
            dealerSeat !== 1 &&
            dealerSeat !== 2 &&
            dealerSeat !== 3)
        )
          return [];
        const dealt = shuffleAndDeal(seed, dealerSeat).hands;
        const hands = Object.fromEntries(
          ([0, 1, 2, 3] as const).map((seat) => [
            String(seat),
            [...dealt[seat]],
          ]),
        ) as RevealedHands;
        return [[String(roundNumber), hands] as const];
      }),
    ) as RevealedHandsByRound;
    return Promise.resolve({
      events: this.game.events.map((event) => ({
        seq: event.seq,
        type: event.type,
        payload: structuredClone(event.payload),
        createdAt: event.at,
      })),
      revealedHandsByRound,
    });
  }

  subscribe(listener: (snapshot: ViewerSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeEvents(listener: (event: AuthorityEvent) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  private publish(snapshot: ViewerSnapshot): void {
    for (const listener of this.listeners) listener(structuredClone(snapshot));
  }

  private publishNewEvents(): void {
    for (const event of this.game.events) {
      if (event.seq <= this.lastPublishedEventSeq) continue;
      const publicEvent: AuthorityEvent = {
        seq: event.seq,
        type: event.type,
        payload: structuredClone(event.payload),
        createdAt: event.at,
      };
      for (const listener of this.eventListeners)
        listener(structuredClone(publicEvent));
      this.lastPublishedEventSeq = event.seq;
    }
  }

  private apply(action: ClientAction): void {
    if (action.type === "PAUSE") {
      if (this.paused) throw new Error("牌局已经暂停");
      this.paused = true;
      this.pausedAt = Date.now();
      this.roomVersion += 1;
      return;
    }
    if (action.type === "RESUME") {
      if (!this.paused || this.pausedAt === null)
        throw new Error("牌局尚未暂停");
      const duration = Date.now() - this.pausedAt;
      this.game = Object.freeze({
        ...this.game,
        deadlineAt:
          this.game.deadlineAt === null
            ? null
            : this.game.deadlineAt + duration,
      });
      this.paused = false;
      this.pausedAt = null;
      this.roomVersion += 1;
      return;
    }
    if (action.type === "HOST_MUTE_TABLE") {
      if (this.tableMuted === action.muted) return;
      this.tableMuted = action.muted;
      this.roomVersion += 1;
      return;
    }
    if (action.type === "SET_TRUSTEE") {
      this.selfTrustee = action.enabled;
      this.game = applyGameAction(this.game, {
        type: "SET_CONTROL",
        seat: 0,
        control: action.enabled ? "bot" : "human",
        at: Date.now(),
      }).state;
      this.roomVersion += 1;
      if (!action.enabled) this.clearAutomationTimer();
      return;
    }
    if (
      this.paused &&
      !["QUICK_PHRASE", "EMOTE", "REQUEST_RECONNECT"].includes(action.type)
    )
      throw new Error("牌局暂停中");
    let coreAction: GameAction | null = null;
    if (action.type === "PLAY_CARDS")
      coreAction = {
        type: "PLAY",
        seat: 0,
        cardIds: action.cardIds as readonly CardId[],
        at: Date.now(),
      };
    else if (action.type === "PASS")
      coreAction = { type: "PASS", seat: 0, at: Date.now() };
    else if (action.type === "OFFER_TRIBUTE")
      coreAction = {
        type: "TRIBUTE",
        seat: 0,
        cardId: action.cardId as CardId,
        at: Date.now(),
      };
    else if (action.type === "RETURN_TRIBUTE")
      coreAction = {
        type: "RETURN_TRIBUTE",
        seat: 0,
        to: action.to as Seat,
        cardId: action.cardId as CardId,
        at: Date.now(),
      };
    if (coreAction) this.game = applyGameAction(this.game, coreAction).state;
    this.roomVersion += 1;
  }

  private runBotsUntilHuman(maxActions = 1): void {
    if (this.paused) return;
    let actions = 0;
    for (let count = 0; count < 2_000; count += 1) {
      if (this.game.phase === "match-settled") return;
      if (this.game.phase === "settled") {
        this.game = applyGameAction(this.game, {
          type: "START_NEXT_ROUND",
          at: Date.now(),
        }).state;
        this.roomVersion += 1;
        continue;
      }
      const actor =
        pendingTribute(this.game)?.actorSeat ?? this.game.currentSeat;
      if (actor === null) return;
      if (actor === 0 && !this.selfTrustee) return;
      if (actions >= maxActions) return;
      const decision = chooseBotAction(createBotObservation(this.game, actor), {
        difficulty: this.botDifficulty,
        seed: `${this.game.matchId}:${this.game.roundNumber}:${this.game.version}:${actor}`,
        timeBudgetMs: this.botDifficulty === "master" ? 12 : 4,
        maxSamples: this.botDifficulty === "master" ? 3 : 0,
      });
      this.game = applyGameAction(this.game, {
        ...decision.action,
        at: Date.now(),
      }).state;
      actions += 1;
      this.roomVersion += 1;
    }
    throw new Error("本地权威牌桌超过自动动作上限");
  }

  private startNextRoundIfNeeded(): void {
    if (this.game.phase !== "settled") return;
    this.game = applyGameAction(this.game, {
      type: "START_NEXT_ROUND",
      at: Date.now(),
    }).state;
    this.roomVersion += 1;
  }

  private scheduleAutomation(): void {
    if (this.closed || this.paused || this.automationTimer !== null) return;
    const actor = pendingTribute(this.game)?.actorSeat ?? this.game.currentSeat;
    if (actor === null || (actor === 0 && !this.selfTrustee)) return;
    this.automationTimer = globalThis.setTimeout(() => {
      this.automationTimer = null;
      if (this.closed || this.paused) return;
      this.runBotsUntilHuman(1);
      const snapshot = this.project();
      this.publish(snapshot);
      this.publishNewEvents();
      this.scheduleAutomation();
    }, 650);
  }

  private clearAutomationTimer(): void {
    if (this.automationTimer !== null)
      globalThis.clearTimeout(this.automationTimer);
    this.automationTimer = null;
  }

  private project(): ViewerSnapshot {
    const tributeAction = pendingTribute(this.game);
    const players: PublicPlayer[] = ([0, 1, 2, 3] as const).map((seat) => ({
      userId: seat === 0 ? "local-player" : `bot-${this.botDifficulty}-${seat}`,
      nickname: seat === 0 ? this.selfNickname : names[seat],
      seat,
      team: seat % 2,
      remainingCards: this.game.players[seat].remainingCards,
      connected: true,
      isBot: seat !== 0 || this.selfTrustee,
      finishOrder: this.game.players[seat].finishOrder,
      voiceActive: false,
      avatar: seat === 0 ? AvatarConfigSchema.parse(this.selfAvatar) : null,
    }));
    return {
      roomId: this.roomId,
      ownerUserId: "local-player",
      tableMuted: this.tableMuted,
      version: this.roomVersion,
      phase: this.game.phase === "match-settled" ? "settled" : this.game.phase,
      players,
      selfSeat: 0,
      selfHand: [...this.game.hands[0]],
      currentSeat: tributeAction?.actorSeat ?? this.game.currentSeat,
      deadlineAt: this.paused ? null : this.game.deadlineAt,
      serverNow: Date.now(),
      levelRank: this.game.levelRank,
      teamLevels: [this.game.teamLevels[0], this.game.teamLevels[1]],
      lastPlay: this.game.lastPlay
        ? {
            seat: this.game.lastPlay.seat,
            cardIds: [...this.game.lastPlay.cardIds],
            combination:
              combinationLabels[this.game.lastPlay.combination.kind] ??
              this.game.lastPlay.combination.kind,
            turnIndex: this.game.lastPlay.turnIndex,
          }
        : null,
      passCount: this.game.passCount,
      paused: this.paused,
      tributeAction,
      eventSeq: this.game.events.length - 1,
    };
  }
}

type TimerHandle = ReturnType<typeof globalThis.setTimeout>;

export interface RemoteTableAuthorityOptions {
  readonly createWebSocket?: (url: string) => WebSocket;
  readonly fetch?: typeof globalThis.fetch;
  readonly createActionId?: () => string;
  readonly reconnectDelaysMs?: readonly number[];
  readonly connectionTimeoutMs?: number;
  readonly actionTimeoutMs?: number;
}

interface PendingRemoteAction {
  readonly action: ClientAction;
  readonly resolve: (snapshot: ViewerSnapshot) => void;
  readonly reject: (error: Error) => void;
  acknowledged: boolean;
  duplicateProbe: boolean;
  timeout: TimerHandle | null;
}

interface SocketAttempt {
  readonly socket: WebSocket;
  readonly reconnecting: boolean;
  readonly reconnectFromEventSeq: number;
  reconnectActionId: string | null;
  reconnectAcknowledged: boolean;
  catchupEventSeq: number;
  receivedInitialSnapshot: boolean;
  settled: boolean;
  timeout: TimerHandle | null;
  readonly resolve: (snapshot: ViewerSnapshot) => void;
  readonly reject: (error: Error) => void;
}

type RemoteConnectionState =
  "idle" | "connecting" | "ready" | "recovering" | "suspended" | "closed";

const DEFAULT_RECONNECT_DELAYS_MS = [250, 500, 1_000, 2_000, 4_000] as const;

export class RemoteTableAuthority implements TableAuthority {
  private socket: WebSocket | null = null;
  private snapshot: ViewerSnapshot | null = null;
  private pending: PendingRemoteAction | null = null;
  private readonly listeners = new Set<(snapshot: ViewerSnapshot) => void>();
  private readonly eventListeners = new Set<(event: AuthorityEvent) => void>();
  private readonly createWebSocket: (url: string) => WebSocket;
  private readonly fetcher: typeof globalThis.fetch;
  private readonly createActionId: () => string;
  private readonly reconnectDelaysMs: readonly number[];
  private readonly connectionTimeoutMs: number;
  private readonly actionTimeoutMs: number;
  private state: RemoteConnectionState = "idle";
  private connectionPromise: Promise<ViewerSnapshot> | null = null;
  private transportEpoch = 0;

  constructor(
    private readonly baseUrl: string,
    private readonly apiUrl: string,
    private readonly roomId: string,
    private readonly token: string,
    options: RemoteTableAuthorityOptions = {},
  ) {
    this.createWebSocket =
      options.createWebSocket ?? ((url) => new WebSocket(url));
    this.fetcher = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.createActionId = options.createActionId ?? (() => crypto.randomUUID());
    this.reconnectDelaysMs =
      options.reconnectDelaysMs ?? DEFAULT_RECONNECT_DELAYS_MS;
    this.connectionTimeoutMs = options.connectionTimeoutMs ?? 8_000;
    this.actionTimeoutMs = options.actionTimeoutMs ?? 6_000;
  }

  async connect(): Promise<ViewerSnapshot> {
    if (this.state === "closed") throw new Error("牌桌连接已关闭");
    if (this.state === "suspended") throw new Error("牌桌连接已暂停");
    if (
      this.state === "ready" &&
      this.socket?.readyState === 1 &&
      this.snapshot
    ) {
      return structuredClone(this.snapshot);
    }
    if (this.connectionPromise) return await this.connectionPromise;
    const reconnecting = this.snapshot !== null;
    const reconnectFromEventSeq = this.snapshot?.eventSeq ?? -1;
    return await this.startConnection(reconnecting, reconnectFromEventSeq);
  }

  async send(action: ClientAction): Promise<ViewerSnapshot> {
    if (this.state === "closed") throw new Error("牌桌连接已关闭");
    if (this.pending) throw new Error("上一动作仍在等待服务端确认");
    await this.connect();
    if (this.hasPendingAction()) throw new Error("上一动作仍在等待服务端确认");
    if (
      !this.socket ||
      this.socket.readyState !== 1 ||
      this.state !== "ready"
    ) {
      throw new Error("牌桌尚未连接，请稍后重试");
    }
    return await new Promise<ViewerSnapshot>((resolve, reject) => {
      this.pending = {
        action,
        resolve,
        reject,
        acknowledged: false,
        duplicateProbe: false,
        timeout: null,
      };
      this.transmitPending();
    });
  }

  subscribe(listener: (snapshot: ViewerSnapshot) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  subscribeEvents(listener: (event: AuthorityEvent) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  async replay(): Promise<AuthorityReplay> {
    const response = await fetch(
      `${this.apiUrl.replace(/\/$/u, "")}/rooms/${encodeURIComponent(this.roomId)}/replay`,
      {
        headers: { authorization: `Bearer ${this.token}` },
      },
    );
    if (!response.ok) throw new Error("无法读取权威事件回放");
    const parsed = ReplayManifestSchema.safeParse(await response.json());
    if (!parsed.success) throw new Error("权威事件回放格式无效");
    if (!parsed.data.final || !parsed.data.participantAuthorized)
      throw new Error("牌局尚未结算或当前用户不是参与者");
    return {
      events: parsed.data.events,
      ...(parsed.data.revealedHandsByRound
        ? { revealedHandsByRound: parsed.data.revealedHandsByRound }
        : parsed.data.revealedHands
          ? {
              revealedHandsByRound: {
                [String(lastReplayRound(parsed.data.events))]:
                  parsed.data.revealedHands,
              },
            }
          : {}),
    };
  }

  close(): void {
    if (this.state === "closed") return;
    this.state = "closed";
    this.transportEpoch += 1;
    const socket = this.socket;
    this.socket = null;
    this.rejectPending(new Error("牌桌连接已关闭，未确认动作没有自动重复执行"));
    socket?.close();
    this.listeners.clear();
    this.eventListeners.clear();
  }

  /** Pauses only the transport. State/listeners/pending idempotency are retained. */
  suspend(): void {
    if (this.state === "closed" || this.state === "suspended") return;
    this.transportEpoch += 1;
    this.state = "suspended";
    const socket = this.socket;
    this.socket = null;
    this.connectionPromise = null;
    this.clearPendingTimeout();
    try {
      socket?.close();
    } catch {
      /* The transport is already unavailable. */
    }
  }

  /** Reissues a one-use socket ticket and reconnects from the last event seq. */
  async resume(): Promise<ViewerSnapshot> {
    if (this.state === "closed") throw new Error("牌桌连接已关闭");
    if (this.state !== "suspended") return this.connect();
    this.state = "idle";
    return this.connect();
  }

  private startConnection(
    reconnecting: boolean,
    reconnectFromEventSeq: number,
  ): Promise<ViewerSnapshot> {
    this.state = reconnecting ? "recovering" : "connecting";
    const epoch = this.transportEpoch;
    const operation = this.establishWithBackoff(
      reconnecting,
      reconnectFromEventSeq,
      epoch,
    )
      .then((snapshot) => {
        if (!this.isCurrentTransport(epoch))
          throw new Error(
            this.state === "suspended" ? "牌桌连接已暂停" : "牌桌连接已关闭",
          );
        this.state = "ready";
        if (reconnecting) this.recoverPending(snapshot);
        return structuredClone(snapshot);
      })
      .catch((error: unknown) => {
        const normalized =
          error instanceof Error ? error : new Error("无法连接牌桌服务");
        if (this.isCurrentTransport(epoch)) {
          this.state = "idle";
          this.socket = null;
          this.rejectPending(
            new Error(
              `牌桌连接中断且重连失败：${normalized.message}；未确认动作没有自动重复执行`,
            ),
          );
        }
        throw normalized;
      })
      .finally(() => {
        if (this.connectionPromise === operation) this.connectionPromise = null;
      });
    this.connectionPromise = operation;
    // Unexpected disconnects start recovery without a direct caller. Attaching
    // a terminal handler prevents a bounded recovery failure becoming an
    // unhandled promise rejection; connect()/send() still receive the error.
    void operation.catch(() => undefined);
    return operation;
  }

  private async establishWithBackoff(
    reconnecting: boolean,
    reconnectFromEventSeq: number,
    epoch: number,
  ): Promise<ViewerSnapshot> {
    let lastError = new Error("无法连接牌桌服务");
    const attemptCount = reconnecting
      ? Math.max(1, this.reconnectDelaysMs.length)
      : this.reconnectDelaysMs.length + 1;
    for (let attemptIndex = 0; attemptIndex < attemptCount; attemptIndex += 1) {
      if (!this.isCurrentTransport(epoch))
        throw new Error(
          this.state === "suspended" ? "牌桌连接已暂停" : "牌桌连接已关闭",
        );
      if (reconnecting || attemptIndex > 0) {
        const delayIndex = reconnecting ? attemptIndex : attemptIndex - 1;
        const delay = this.reconnectDelaysMs[delayIndex] ?? 0;
        await new Promise<void>((resolve) =>
          globalThis.setTimeout(resolve, delay),
        );
        if (!this.isCurrentTransport(epoch))
          throw new Error(
            this.state === "suspended" ? "牌桌连接已暂停" : "牌桌连接已关闭",
          );
      }
      try {
        return await this.openSocket(
          reconnecting,
          reconnectFromEventSeq,
          epoch,
        );
      } catch (error) {
        lastError = error instanceof Error ? error : lastError;
      }
    }
    throw new Error(`已达到重连次数上限（${lastError.message}）`);
  }

  private async openSocket(
    reconnecting: boolean,
    reconnectFromEventSeq: number,
    epoch: number,
  ): Promise<ViewerSnapshot> {
    const ticket = await this.issueSocketTicket();
    if (!this.isCurrentTransport(epoch))
      throw new Error(
        this.state === "suspended" ? "牌桌连接已暂停" : "牌桌连接已关闭",
      );
    return new Promise<ViewerSnapshot>((resolve, reject) => {
      const url = `${this.baseUrl.replace(/\/$/u, "")}/rooms/${encodeURIComponent(this.roomId)}/connect?ticket=${encodeURIComponent(ticket)}`;
      const socket = this.createWebSocket(url);
      this.socket = socket;
      const attempt: SocketAttempt = {
        socket,
        reconnecting,
        reconnectFromEventSeq,
        reconnectActionId: null,
        reconnectAcknowledged: false,
        catchupEventSeq: reconnectFromEventSeq,
        receivedInitialSnapshot: false,
        settled: false,
        timeout: null,
        resolve,
        reject,
      };
      attempt.timeout = globalThis.setTimeout(() => {
        this.failAttempt(attempt, new Error("连接牌桌超时"));
      }, this.connectionTimeoutMs);
      socket.addEventListener("message", (event) =>
        this.handleSocketMessage(attempt, event),
      );
      socket.addEventListener("close", () =>
        this.handleSocketClosed(attempt, new Error("牌桌连接已断开")),
      );
      socket.addEventListener("error", () =>
        this.handleSocketClosed(attempt, new Error("无法连接牌桌服务")),
      );
    });
  }

  private async issueSocketTicket(): Promise<string> {
    const response = await this.fetcher(
      `${this.apiUrl.replace(/\/$/u, "")}/rooms/${encodeURIComponent(this.roomId)}/socket-ticket`,
      { method: "POST", headers: { authorization: `Bearer ${this.token}` } },
    );
    if (!response.ok) throw new Error("无法获取牌桌连接票据");
    const value: unknown = await response.json();
    if (!value || typeof value !== "object")
      throw new Error("牌桌连接票据格式无效");
    const ticket = (value as { ticket?: unknown }).ticket;
    const expiresAt = (value as { expiresAt?: unknown }).expiresAt;
    if (
      typeof ticket !== "string" ||
      !/^[A-Za-z0-9_-]{32,128}$/u.test(ticket) ||
      typeof expiresAt !== "number"
    ) {
      throw new Error("牌桌连接票据格式无效");
    }
    return ticket;
  }

  private handleSocketMessage(
    attempt: SocketAttempt,
    event: MessageEvent,
  ): void {
    if (attempt.socket !== this.socket || this.state === "closed") return;
    let input: unknown;
    try {
      input = JSON.parse(String(event.data));
    } catch {
      return;
    }
    const parsed = ServerMessageSchema.safeParse(input);
    if (!parsed.success) {
      this.failAttempt(attempt, new Error("服务端牌桌消息无效"));
      return;
    }
    const message = parsed.data;
    if (message.type === "SNAPSHOT") {
      this.handleSnapshot(attempt, message.snapshot);
      return;
    }
    if (message.type === "EVENT") {
      if (attempt.reconnecting && message.event.seq > attempt.catchupEventSeq) {
        if (message.event.seq !== attempt.catchupEventSeq + 1) {
          this.failAttempt(attempt, new Error("断线补流事件不连续"));
          return;
        }
        attempt.catchupEventSeq = message.event.seq;
      }
      for (const listener of this.eventListeners)
        listener(structuredClone(message.event));
      return;
    }
    if (message.type === "ACK") {
      this.handleAcknowledgement(attempt, message.actionId);
      return;
    }
    if (message.type === "ERROR") this.handleServerError(attempt, message);
  }

  private handleSnapshot(attempt: SocketAttempt, input: ViewerSnapshot): void {
    const parsed = ViewerSnapshotSchema.safeParse(input);
    if (!parsed.success) {
      this.failAttempt(attempt, new Error("服务端牌桌状态无效"));
      return;
    }
    const snapshot = parsed.data;
    this.publishSnapshot(snapshot);
    if (!attempt.receivedInitialSnapshot) {
      attempt.receivedInitialSnapshot = true;
      if (attempt.reconnecting) {
        this.sendReconnectRequest(attempt, snapshot);
      } else {
        this.completeAttempt(attempt, snapshot);
      }
      return;
    }
    if (
      !attempt.settled &&
      attempt.reconnecting &&
      attempt.reconnectAcknowledged
    ) {
      if (snapshot.eventSeq < attempt.catchupEventSeq) {
        this.failAttempt(attempt, new Error("断线补流快照不完整"));
        return;
      }
      this.completeAttempt(attempt, snapshot);
      return;
    }
    if (attempt.settled && this.pending?.acknowledged)
      this.resolvePending(snapshot);
  }

  private sendReconnectRequest(
    attempt: SocketAttempt,
    snapshot: ViewerSnapshot,
  ): void {
    const reconnectAction: ClientAction = {
      type: "REQUEST_RECONNECT",
      actionId: this.createActionId(),
      expectedVersion: snapshot.version,
      roomId: this.roomId,
      lastEventSeq: attempt.reconnectFromEventSeq,
    };
    attempt.reconnectActionId = reconnectAction.actionId;
    try {
      attempt.socket.send(JSON.stringify(reconnectAction));
    } catch {
      this.failAttempt(attempt, new Error("无法发送断线恢复请求"));
    }
  }

  private handleAcknowledgement(
    attempt: SocketAttempt,
    actionId: string,
  ): void {
    if (actionId === attempt.reconnectActionId) {
      attempt.reconnectAcknowledged = true;
      return;
    }
    if (actionId !== this.pending?.action.actionId) return;
    this.pending.acknowledged = true;
    this.clearPendingTimeout();
    if (this.pending.duplicateProbe && this.snapshot)
      this.resolvePending(this.snapshot);
  }

  private handleServerError(
    attempt: SocketAttempt,
    message: Extract<ServerMessage, { type: "ERROR" }>,
  ): void {
    if (message.snapshot) this.publishSnapshot(message.snapshot);
    const error = new Error(message.message || "牌桌动作失败");
    if (
      message.actionId === attempt.reconnectActionId ||
      (!message.actionId && !attempt.settled)
    ) {
      this.failAttempt(attempt, new Error(`断线恢复失败：${error.message}`));
      return;
    }
    if (
      !this.pending ||
      (message.actionId && message.actionId !== this.pending.action.actionId)
    )
      return;
    const uncertain = this.pending.duplicateProbe;
    this.rejectPending(
      new Error(
        uncertain
          ? `动作未自动重复执行，牌桌状态已同步：${error.message}`
          : error.message,
      ),
    );
  }

  private completeAttempt(
    attempt: SocketAttempt,
    snapshot: ViewerSnapshot,
  ): void {
    if (attempt.settled) return;
    attempt.settled = true;
    if (attempt.timeout !== null) globalThis.clearTimeout(attempt.timeout);
    attempt.timeout = null;
    attempt.resolve(structuredClone(snapshot));
  }

  private failAttempt(attempt: SocketAttempt, error: Error): void {
    if (attempt.settled) {
      this.handleUnexpectedDisconnect(attempt.socket);
      return;
    }
    attempt.settled = true;
    if (attempt.timeout !== null) globalThis.clearTimeout(attempt.timeout);
    attempt.timeout = null;
    if (attempt.socket === this.socket) this.socket = null;
    try {
      attempt.socket.close();
    } catch {
      /* Socket is already unusable. */
    }
    attempt.reject(error);
  }

  private handleSocketClosed(attempt: SocketAttempt, error: Error): void {
    if (this.state === "closed" || attempt.socket !== this.socket) return;
    if (!attempt.settled) {
      this.failAttempt(attempt, error);
      return;
    }
    this.handleUnexpectedDisconnect(attempt.socket);
  }

  private handleUnexpectedDisconnect(socket: WebSocket): void {
    if (this.state === "closed" || socket !== this.socket) return;
    this.socket = null;
    try {
      socket.close();
    } catch {
      /* The transport is already unavailable. */
    }
    this.clearPendingTimeout();
    const reconnectFromEventSeq = this.snapshot?.eventSeq ?? -1;
    void this.startConnection(true, reconnectFromEventSeq);
  }

  private recoverPending(snapshot: ViewerSnapshot): void {
    if (!this.pending) return;
    if (this.pending.acknowledged) {
      this.resolvePending(snapshot);
      return;
    }
    this.pending.duplicateProbe =
      this.pending.action.expectedVersion !== snapshot.version;
    this.transmitPending();
  }

  private transmitPending(): void {
    if (!this.pending) return;
    if (
      !this.socket ||
      this.socket.readyState !== 1 ||
      this.state !== "ready"
    ) {
      if (!this.connectionPromise && this.state !== "closed") {
        const reconnectFromEventSeq = this.snapshot?.eventSeq ?? -1;
        void this.startConnection(
          this.snapshot !== null,
          reconnectFromEventSeq,
        );
      }
      return;
    }
    try {
      this.socket.send(JSON.stringify(this.pending.action));
    } catch {
      this.handleUnexpectedDisconnect(this.socket);
      return;
    }
    this.clearPendingTimeout();
    this.pending.timeout = globalThis.setTimeout(() => {
      if (!this.pending) return;
      const socket = this.socket;
      if (!socket) return;
      this.clearPendingTimeout();
      try {
        socket.close();
      } catch {
        /* The close handler performs recovery. */
      }
      this.handleUnexpectedDisconnect(socket);
    }, this.actionTimeoutMs);
  }

  private publishSnapshot(snapshot: ViewerSnapshot): void {
    this.snapshot = structuredClone(snapshot);
    for (const listener of this.listeners) listener(structuredClone(snapshot));
  }

  private resolvePending(snapshot: ViewerSnapshot): void {
    const pending = this.pending;
    if (!pending) return;
    this.clearPendingTimeout();
    this.pending = null;
    pending.resolve(structuredClone(snapshot));
  }

  private rejectPending(error: Error): void {
    const pending = this.pending;
    if (!pending) return;
    this.clearPendingTimeout();
    this.pending = null;
    pending.reject(error);
  }

  private clearPendingTimeout(): void {
    if (this.pending?.timeout !== null && this.pending?.timeout !== undefined) {
      globalThis.clearTimeout(this.pending.timeout);
      this.pending.timeout = null;
    }
  }

  private isCurrentTransport(epoch: number): boolean {
    return (
      this.transportEpoch === epoch &&
      this.state !== "closed" &&
      this.state !== "suspended"
    );
  }

  private hasPendingAction(): boolean {
    return this.pending !== null;
  }
}

export function createTableAuthority(
  roomId: string,
  difficulty: BotDifficulty = "expert",
  selfNickname = "本机玩家",
  selfAvatar: AvatarConfig = DEFAULT_AVATAR_CONFIG,
  ruleProfile: Partial<GuandanRuleProfile> = { finishMode: "through-ace" },
): TableAuthority {
  const url = import.meta.env.VITE_GAME_WS_URL as string | undefined;
  const apiUrl = import.meta.env.VITE_GAME_API_URL as string | undefined;
  const token = getSessionToken();
  const isServerRoom = /^[0-9a-f]{8}-[0-9a-f-]{27}$/iu.test(roomId);
  return url && apiUrl && token && isServerRoom
    ? new RemoteTableAuthority(url, apiUrl, roomId, token)
    : new LocalTableAuthority(
        roomId,
        difficulty,
        selfNickname,
        selfAvatar,
        ruleProfile,
      );
}

export type HintStrategy =
  | "minimum-beat"
  | "least-split"
  | "preserve-bombs"
  | "help-partner"
  | "fast-finish";

export interface HintStrategyDefinition {
  readonly id: HintStrategy;
  readonly label: string;
  readonly description: string;
}

export const HINT_STRATEGIES: readonly HintStrategyDefinition[] = Object.freeze(
  [
    {
      id: "minimum-beat",
      label: "最小压制",
      description: "选择点力最低的非炸弹合法牌，刚好压过桌面牌。",
    },
    {
      id: "least-split",
      label: "最少拆牌",
      description: "优先选择不拆散当前智能组合的合法牌。",
    },
    {
      id: "preserve-bombs",
      label: "保留炸弹",
      description: "优先避开能组成炸弹或同花顺的实体牌。",
    },
    {
      id: "help-partner",
      label: "帮助队友",
      description: "根据公开的队友领出和对手余牌选择控场牌。",
    },
    {
      id: "fast-finish",
      label: "快速走牌",
      description: "优先一次走出更多牌张，尽快减少手牌。",
    },
  ],
);

export interface LegalHint {
  readonly strategy: HintStrategy;
  readonly strategyLabel: string;
  readonly description: string;
  readonly cardIds: readonly string[];
  readonly combinationKind: string;
}

export function chooseLegalHint(
  snapshot: ViewerSnapshot,
  locked: ReadonlySet<string>,
  strategy: HintStrategy,
): LegalHint | null {
  const hand = snapshot.selfHand.filter(
    (cardId) => !locked.has(cardId),
  ) as CardId[];
  if (hand.length === 0) return null;
  const level = levelRank(snapshot.levelRank);
  const definition =
    HINT_STRATEGIES.find((candidate) => candidate.id === strategy) ??
    HINT_STRATEGIES[0];
  if (!definition) return null;
  if (
    snapshot.phase === "tribute" &&
    snapshot.tributeAction?.actorSeat === snapshot.selfSeat
  ) {
    if (snapshot.tributeAction.kind === "offer") {
      const cardId = legalTributeCards(hand, level)[0];
      return cardId
        ? {
            strategy,
            strategyLabel: definition.label,
            description: "进贡阶段已排除红桃级牌，并选择可进贡的最大牌。",
            cardIds: [cardId],
            combinationKind: "single",
          }
        : null;
    }
    const cardId = legalReturnTributeCards(hand, level)[0];
    return cardId
      ? {
          strategy,
          strategyLabel: definition.label,
          description:
            "还贡阶段优先选择十点及以下的最低牌；没有时选择全手最小牌。",
          cardIds: [cardId],
          combinationKind: "single",
        }
      : null;
  }
  if (snapshot.phase !== "playing") return null;
  const incumbent = snapshot.lastPlay
    ? classifyCombination(snapshot.lastPlay.cardIds as readonly CardId[], {
        levelRank: level,
      })
    : null;
  const legal = enumerateLegalPlays(
    hand,
    incumbent,
    { levelRank: level },
    { physicalVariants: false },
  );
  if (legal.length === 0) return null;

  const allLeadingPlays = enumerateLegalPlays(
    hand,
    null,
    { levelRank: level },
    { physicalVariants: false },
  );
  const structuralOptions = allLeadingPlays.filter(
    (play) => play.cardIds.length > 1,
  );
  const protectedBombCards = new Set<string>(
    allLeadingPlays
      .filter((play) => play.combination.isBomb)
      .flatMap((play) => play.cardIds),
  );
  const chosen = legal
    .map((play) => ({
      play,
      score: strategyScore(
        play,
        strategy,
        snapshot,
        hand,
        structuralOptions,
        protectedBombCards,
        level,
      ),
    }))
    .sort((left, right) => {
      const score = compareNumericScores(left.score, right.score);
      if (score !== 0) return score;
      return left.play.cardIds
        .join(",")
        .localeCompare(right.play.cardIds.join(","), undefined, {
          numeric: true,
        });
    })[0]?.play;
  return chosen
    ? {
        strategy,
        strategyLabel: definition.label,
        description: definition.description,
        cardIds: chosen.cardIds,
        combinationKind: chosen.combination.kind,
      }
    : null;
}

export function nextLegalHint(
  snapshot: ViewerSnapshot,
  locked: ReadonlySet<string>,
  cursor: number,
): readonly string[] {
  const strategy =
    HINT_STRATEGIES[
      ((cursor % HINT_STRATEGIES.length) + HINT_STRATEGIES.length) %
        HINT_STRATEGIES.length
    ]?.id ?? "minimum-beat";
  return chooseLegalHint(snapshot, locked, strategy)?.cardIds ?? [];
}

function strategyScore(
  play: LegalPlay,
  strategy: HintStrategy,
  snapshot: ViewerSnapshot,
  hand: readonly CardId[],
  structuralOptions: readonly LegalPlay[],
  protectedBombCards: ReadonlySet<string>,
  level: NaturalRank,
): readonly number[] {
  const bomb = Number(play.combination.isBomb);
  const mainValue = play.combination.mainValue;
  const cardCount = play.cardIds.length;
  if (strategy === "minimum-beat") {
    return [bomb, mainValue, cardCount];
  }
  if (strategy === "least-split") {
    return [
      structuralSplitCost(play, structuralOptions),
      cardCount === 1 ? 0 : 1,
      bomb,
      mainValue,
    ];
  }
  if (strategy === "preserve-bombs") {
    const protectedOverlap = play.cardIds.filter((cardId) =>
      protectedBombCards.has(cardId),
    ).length;
    return [protectedOverlap, bomb, mainValue, cardCount];
  }
  if (strategy === "help-partner") {
    const self = snapshot.players.find(
      (player) => player.seat === snapshot.selfSeat,
    );
    const partner = snapshot.players.find(
      (player) =>
        player.team === self?.team && player.seat !== snapshot.selfSeat,
    );
    const partnerLed = snapshot.lastPlay?.seat === partner?.seat;
    const opponentThreat = snapshot.players.some(
      (player) =>
        player.team !== self?.team &&
        player.finishOrder === null &&
        player.remainingCards <= 5,
    );
    if (
      partnerLed &&
      (partner?.remainingCards ?? Number.POSITIVE_INFINITY) <= 5
    ) {
      return [bomb, mainValue, cardCount];
    }
    if (opponentThreat) return [bomb, -cardCount, mainValue];
    return [
      bomb,
      -cardCount,
      mainValue,
      structuralSplitCost(play, structuralOptions),
    ];
  }

  const remaining = hand.length - cardCount;
  const levelCardCount = play.cardIds.filter(
    (cardId) => getCard(cardId).rank === level,
  ).length;
  return [remaining, -bomb, -levelCardCount, -mainValue];
}

function structuralSplitCost(
  play: LegalPlay,
  structuralOptions: readonly LegalPlay[],
): number {
  const selected = new Set<string>(play.cardIds);
  let cost = 0;
  for (const option of structuralOptions) {
    const overlap = option.cardIds.filter((cardId) =>
      selected.has(cardId),
    ).length;
    if (overlap > 0 && overlap < option.cardIds.length) {
      cost += option.cardIds.length * 10 + overlap;
    }
  }
  return cost;
}

function compareNumericScores(
  left: readonly number[],
  right: readonly number[],
): number {
  const length = Math.max(left.length, right.length);
  for (let index = 0; index < length; index += 1) {
    const difference = (left[index] ?? 0) - (right[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

export function makeAction(
  snapshot: ViewerSnapshot,
  action: ClientIntent,
): ClientAction {
  return {
    ...action,
    actionId: crypto.randomUUID(),
    expectedVersion: snapshot.version,
    roomId: snapshot.roomId,
  };
}
